import React from 'react';
import { Row, Col, Form, Button, Alert, InputGroup, Modal } from 'react-bootstrap';
import { IonIcon } from '@ionic/react';
import { eye, eyeOff } from 'ionicons/icons';
import { BOARDS } from '../../constants/deviceConfig';
import OtaMigrationAlert from './OtaMigrationAlert';
import OtaProgressPanel from './OtaProgressPanel';

const DeviceConfigPanel = ({
  deviceType,
  setDeviceType,
  board,
  setBoard,
  wifiSsid,
  setWifiSsid,
  wifiPassword,
  setWifiPassword,
  showPassword,
  setShowPassword,
  progress,
  programStatus,
  onProgramDevice,
  onPrepareOta,
  onAbortOta,
  otaAllowed = false,
  otaInFlight,
  otaState,
  onClearOta,
  showAdvancedSettings,
  setShowAdvancedSettings,
  baudrate,
  setBaudrate,
  flashMode,
  setFlashMode,
  flashFreq,
  setFlashFreq,
  flashSize,
  setFlashSize,
}) => (
  <div className="setup-panel">
    <h4 className="mb-3">Device Configuration</h4>
    <Row className="g-3 align-items-end">
      <Col md={3}>
        <Form.Label>Core</Form.Label>
        <Form.Select
          value={deviceType}
          onChange={(e) => {
            const newType = e.target.value;
            setDeviceType(newType);
            setBoard(Object.keys(BOARDS[newType])[0]);
          }}
        >
          <option value="esp32">ESP32</option>
        </Form.Select>
      </Col>
      <Col md={3}>
        <Form.Label>Board</Form.Label>
        <Form.Select value={board} onChange={(e) => setBoard(e.target.value)}>
          {Object.keys(BOARDS[deviceType]).map((name) => (
            <option key={name}>{name}</option>
          ))}
        </Form.Select>
      </Col>
      <Col md={3}>
        <Form.Label>WiFi SSID</Form.Label>
        <Form.Control
          type="text"
          value={wifiSsid}
          onChange={(e) => setWifiSsid(e.target.value)}
          placeholder="Your network"
        />
      </Col>
      <Col md={3}>
        <Form.Label>WiFi Password</Form.Label>
        <InputGroup>
          <Form.Control
            type={showPassword ? 'text' : 'password'}
            value={wifiPassword}
            onChange={(e) => setWifiPassword(e.target.value)}
            required
          />
          <InputGroup.Text onClick={() => setShowPassword(!showPassword)} className="peek-toggle">
            <IonIcon icon={showPassword ? eyeOff : eye} />
          </InputGroup.Text>
        </InputGroup>
      </Col>
    </Row>

    <p className="text-app-muted small mb-2">
      USB flash requires Chrome or Edge. Click Program Device, approve the USB port when prompted, and wait for compile to finish.
      If connect fails after 3 tries: hold BOOT, tap EN/RESET, release BOOT on the retry prompt. Close Arduino Serial Monitor first.
      Defaults for ESP32 Dev Module: dio / 80m / 4MB / 115200 baud.
    </p>
    <div className="d-flex gap-2 mt-3 align-items-center flex-wrap">
      <Button variant="primary" onClick={onProgramDevice}>Program Device</Button>
      {otaAllowed && (
        <>
          <Button
            onClick={onPrepareOta}
            disabled={otaInFlight}
            style={{
              backgroundColor: 'var(--teal)',
              borderColor: 'var(--teal)',
              color: 'white',
            }}
          >
            🚀 Send OTA Update
          </Button>
          {otaInFlight && (
            <Button variant="outline-danger" onClick={onAbortOta}>
              Cancel OTA
            </Button>
          )}
        </>
      )}
      <Button variant="outline-secondary" onClick={() => setShowAdvancedSettings(true)}>
        Advanced Settings
      </Button>
    </div>

    {otaAllowed && <OtaMigrationAlert />}

    {progress && <Alert variant="info" className="mt-2 mb-0">{progress}</Alert>}
    {programStatus && (
      <div className="mt-2">
        <h6>Programming Output:</h6>
        <pre className="setup-code-block">
          {programStatus}
        </pre>
      </div>
    )}

    {otaAllowed && (
      <OtaProgressPanel
        otaState={otaState}
        otaInFlight={otaInFlight}
        onClear={onClearOta}
      />
    )}

    <Modal show={showAdvancedSettings} onHide={() => setShowAdvancedSettings(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Flash Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Label>Baudrate</Form.Label>
            <Form.Select value={baudrate} onChange={(e) => setBaudrate(Number(e.target.value))}>
              <option value={115200}>115200</option>
              <option value={230400}>230400</option>
              <option value={460800}>460800</option>
              <option value={921600}>921600</option>
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label>Flash Mode</Form.Label>
            <Form.Select value={flashMode} onChange={(e) => setFlashMode(e.target.value)}>
              <option value="qio">qio</option>
              <option value="qout">qout</option>
              <option value="dio">dio</option>
              <option value="dout">dout</option>
            </Form.Select>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Label>Flash Frequency</Form.Label>
            <Form.Select value={flashFreq} onChange={(e) => setFlashFreq(e.target.value)}>
              <option value="40m">40m</option>
              <option value="80m">80m</option>
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label>Flash Size</Form.Label>
            <Form.Select value={flashSize} onChange={(e) => setFlashSize(e.target.value)}>
              <option value="2MB">2MB</option>
              <option value="4MB">4MB</option>
              <option value="8MB">8MB</option>
              <option value="16MB">16MB</option>
            </Form.Select>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowAdvancedSettings(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  </div>
);

export default DeviceConfigPanel;